/**
 * Readable values for the Edge Fleet drawer's "Actually running" section.
 *
 * The agent's self-reported configuration is mostly flat (version, buildSha,
 * scanIntervalS), but two entries are nested: `capabilities` ({scannerKinds,
 * credentialNames}, two lists) and `healthFailureCounts` (reason -> count).
 * The drawer rendered every value with String(), so both read "[object Object]"
 * - the replica proof of release 2.1, laptop TEST-R21B-LAPTOP, 21 Sep 2026.
 *
 * Top-level keys stay as the agent names them (operators match them against
 * config). Keys INSIDE a nested value are put into words; values are never
 * rewritten, because a scanner kind or credential name is an identifier.
 */

/**
 * List items shown before "and N more". High enough for every scanner kind an
 * agent can report (23 in release 2.1), low enough that a host's credential
 * names (up to 200) do not fill the drawer.
 */
export const MAX_LIST_ITEMS = 30;

/** "scannerKinds" -> "scanner kinds", "read_error" -> "read error". */
export function humanizeKey(key: string): string {
  return key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .toLowerCase();
}

export function isNestedValue(value: unknown): value is object {
  return value !== null && typeof value === 'object';
}

/** A primitive exactly as the drawer has always shown it; a missing value in words. */
function primitiveText(value: unknown): string {
  if (value === null || value === undefined) return 'not set';
  if (value === '') return '(empty)';
  return String(value);
}

/** Anything, on one line. Used for list items and for values two levels down. */
function inlineText(value: unknown): string {
  if (Array.isArray(value)) return listText(value);
  if (isNestedValue(value)) {
    const entries = Object.entries(value);
    if (entries.length === 0) return 'none';
    return entries.map(([k, v]) => `${humanizeKey(k)} ${inlineText(v)}`).join(', ');
  }
  return primitiveText(value);
}

/** "local_filesystem, smb, s3", "none", or the first few and "and N more". */
function listText(items: readonly unknown[]): string {
  if (items.length === 0) return 'none';
  const shown = items.slice(0, MAX_LIST_ITEMS).map(inlineText).join(', ');
  const rest = items.length - MAX_LIST_ITEMS;
  return rest > 0 ? `${shown} and ${rest} more` : shown;
}

/**
 * The lines one config value takes. A string, number or boolean is one line,
 * shown as before; a missing value says "not set" instead of "null". A list is
 * one line of its items. An object is one line per entry, "scanner kinds:
 * local_filesystem, smb". Never "[object Object]".
 */
export function configValueLines(value: unknown): string[] {
  if (Array.isArray(value)) return [listText(value)];
  if (isNestedValue(value)) {
    const entries = Object.entries(value);
    if (entries.length === 0) return ['none'];
    return entries.map(([k, v]) => `${humanizeKey(k)}: ${inlineText(v)}`);
  }
  return [primitiveText(value)];
}
